import { getCallbacks, setState, resetState } from './state';
import {
  isValidPaymentMessage,
  createPaymentResult,
  createPaymentResultFromStatus,
  lockBodyScroll,
  unlockBodyScroll,
  copyToClipboard,
} from './utils';
import { getPaymentUrl } from './config';

const MODAL_ID = 'arto-pay-payment-modal';
const BACKDROP_ID = 'arto-pay-payment-backdrop';
const CONTAINER_ID = 'arto-pay-payment-container';
const IFRAME_ID = 'arto-pay-payment-iframe';
const CLOSE_BTN_ID = 'arto-pay-payment-close';
const SPINNER_ID = 'arto-pay-payment-spinner';
const STYLE_ID = 'arto-pay-sdk-styles';

let messageHandler: ((event: MessageEvent) => void) | null = null;
let keyHandler: ((event: KeyboardEvent) => void) | null = null;

// What the page had focused before the modal opened, and which page elements the modal made inert.
let previouslyFocused: HTMLElement | null = null;
let inertElements: Element[] = [];

let initData: {
  clientSecret: string;
  publicKey: string;
  token?: string;
  orderId?: string;
  origin: string;
} | null = null;

/* =========================
   Helpers
========================= */

/**
 * Makes everything except the modal unreachable by keyboard and screen readers, so Tab cannot
 * wander into the page hidden behind the backdrop.
 */
function makeBackgroundInert(modal: HTMLElement): void {
  inertElements = Array.from(document.body.children).filter(
    (el) => el !== modal && !el.hasAttribute('inert')
  );
  inertElements.forEach((el) => el.setAttribute('inert', ''));
}

function restoreBackground(): void {
  inertElements.forEach((el) => el.removeAttribute('inert'));
  inertElements = [];
}

function restoreFocus(): void {
  const target = previouslyFocused;
  previouslyFocused = null;
  if (target && document.contains(target)) target.focus();
}

function sendInitToIframe() {
  const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;
  if (!iframe || !iframe.contentWindow || !initData) return;
  iframe.contentWindow.postMessage(
    {
      type: 'INIT',
      clientSecret: initData.clientSecret,
      publicKey: initData.publicKey,
      token: initData.token,
      orderId: initData.orderId,
    },
    initData.origin
  );
}

/* =========================
   Styles
========================= */

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    #${BACKDROP_ID} {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.6);
      z-index: 999998;
    }

    #${CONTAINER_ID} {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 90%;
      max-width: 600px;
      height: 100%;
      max-height: 90vh;
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      z-index: 999999;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      box-sizing: border-box;

      transition: height 0.2s ease;
    }

    /* Only ever focused from script, so it needs no ring. */
    #${CONTAINER_ID}:focus {
      outline: none;
    }

    /* The banner sits above the iframe, so the sandbox sheet is allowed to be that much taller.
       Capped at the viewport so it can never overflow a short window. */
    #${CONTAINER_ID}[data-sandbox] {
      max-height: min(calc(90vh + 50px), 100%);
    }

    #${CONTAINER_ID} .sandbox-mode-banner {
      background: #FCA604;
      color: white;
      font-size: 16px;
      font-weight: 500;
      text-align: center;
      padding: 16px 8px;
      letter-spacing: 0.5px;
    }

    #${CLOSE_BTN_ID} {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 32px;
      height: 32px;
      border: none;
      background: rgba(0, 0, 0, 0.1);
      border-radius: 50%;
      cursor: pointer;
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      font-weight: bold;
      color: #333;
      line-height: 1;
      /* Merchant pages often style every <button>; keep their padding and sizing out of the sheet. */
      padding: 0;
      margin: 0;
      box-sizing: border-box;
    }

    #${CLOSE_BTN_ID}:hover {
      background: rgba(0, 0, 0, 0.2);
    }

    #${CLOSE_BTN_ID}:focus-visible {
      outline: 2px solid #40a38a;
      outline-offset: 2px;
    }

    /* 32px is under the recommended touch target; widen the hit area without changing how it looks. */
    @media (pointer: coarse) {
      #${CLOSE_BTN_ID}::after {
        content: '';
        position: absolute;
        inset: -6px;
      }
    }

    #${IFRAME_ID} {
      width: 100%;
      height: 100%;
      border: none;
      flex: 1;
    }

    /* Mirrors the in-app redirect overlay loader so the checkout looks the same
       before and after the iframe takes over. */
    #${SPINNER_ID} {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 0 24px;
      text-align: center;
    }

    #${SPINNER_ID} svg {
      width: 32px;
      height: 32px;
      color: #40a38a;
      animation: arto-pay-spin 1s linear infinite;
    }

    #${SPINNER_ID} p {
      margin: 0;
      font-size: 14px;
      line-height: 20px;
      color: #404040;
    }

    @keyframes arto-pay-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    #${SPINNER_ID}.hidden {
      display: none;
    }

    @media (prefers-reduced-motion: reduce) {
      #${CONTAINER_ID} {
        transition: none;
      }
    }

    /*
      On mobile the checkout takes over the whole screen instead of floating.
      !important is required here because both the sandbox banner setup and the
      IFRAME_HEIGHT message write inline styles onto the container.
      The dvh declarations override the % fallback where supported, so the sheet
      is not cut off by the mobile browser toolbars.
    */
    @media (max-width: 640px) {
      #${CONTAINER_ID} {
        top: 0 !important;
        left: 0 !important;
        transform: none !important;
        width: 100% !important;
        max-width: 100% !important;
        height: 100% !important;
        height: 100dvh !important;
        max-height: 100% !important;
        max-height: 100dvh !important;
        border-radius: 0 !important;
      }
    }
  `;
  document.head.appendChild(style);
}

/* =========================
   Spinner
========================= */

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Same lucide `Loader2` arc the payment app uses for its redirect overlay.
 * Built with DOM APIs rather than innerHTML so the SDK keeps working on
 * merchant pages that enforce Trusted Types.
 */
function createSpinner(): HTMLDivElement {
  const spinner = document.createElement('div');
  spinner.id = SPINNER_ID;
  spinner.setAttribute('role', 'status');

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');

  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', 'M21 12a9 9 0 1 1-6.219-8.56');
  svg.appendChild(path);

  const label = document.createElement('p');
  label.textContent = 'Preparing secure checkout...';

  spinner.appendChild(svg);
  spinner.appendChild(label);

  return spinner;
}

/* =========================
   Message Handler
========================= */

function handleMessage(event: MessageEvent): void {
  const callbacks = getCallbacks();
  const container = document.getElementById(CONTAINER_ID);
  const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;

  if (event.source !== iframe?.contentWindow) return;

  if (event.data?.type === "IFRAME_HEIGHT") {
    const height = event.data.height
    if (!container) return;

    const banner = container.querySelector('.sandbox-mode-banner') as HTMLElement | null;
    const bannerHeight = banner ? banner.offsetHeight : 0;
    const finalHeight = height + bannerHeight;

    container.style.height = `${finalHeight}px`;
    return;
  }

  if (event.data?.type === 'URL_CHANGE') {
    iframe?.removeAttribute('scrolling')
    return;
  }


  // iframe bilang siap
  if (event.data?.type === 'READY') {
    sendInitToIframe();
    return;
  }

  if (event.data?.type === 'COPY_TEXT') {
    if (typeof event.data.value === 'string') copyToClipboard(event.data.value);
    return;
  }

  if (event.data?.type === 'PAYMENT_SUCCESS') {
    callbacks.onSuccess?.({
      transactionStatus: 'settlement',
      status: 'success',
      rawData: event.data,
    });
    closeModal(false);
    return;
  }

  if (event.data?.type === 'PAYMENT_RESULT') {
    const result = createPaymentResultFromStatus(String(event.data.status), event.data);

    switch (result.status) {
      case 'success':
        callbacks.onSuccess?.(result);
        closeModal(false);
        break;
      case 'pending':
        // Still in progress: report it and keep the checkout open for the payer to finish.
        callbacks.onPending?.(result);
        break;
      default:
        callbacks.onError?.(result);
        closeModal(false);
    }
    return;
  }

  if (!isValidPaymentMessage(event.data)) return;

  const result = createPaymentResult(event.data);

  switch (result.status) {
    case 'success':
      callbacks.onSuccess?.(result);
      break;
    case 'pending':
      callbacks.onPending?.(result);
      break;
    case 'error':
      callbacks.onError?.(result);
      break;
  }
}

/* =========================
   UI Handlers
========================= */

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') closeModal(true);
}

/* =========================
   Public API
========================= */

export function createModal(
  clientSecret: string,
  paymentId: string,
  publicKey: string,
  token: string | undefined,
  orderId: string | undefined,
  sandbox: boolean
): void {
  if (document.getElementById(MODAL_ID)) return;

  const url = getPaymentUrl(paymentId, sandbox);
  const origin = new URL(url).origin;

  initData = { clientSecret, publicKey, token, orderId, origin };

  injectStyles();
  previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  lockBodyScroll();

  const modal = document.createElement('div');
  modal.id = MODAL_ID;

  const backdrop = document.createElement('div');
  backdrop.id = BACKDROP_ID;

  const container = document.createElement('div');
  container.id = CONTAINER_ID;
  container.setAttribute('role', 'dialog');
  container.setAttribute('aria-modal', 'true');
  container.setAttribute('aria-label', 'Arto Pay checkout');
  container.tabIndex = -1;

  if (sandbox) {
    container.setAttribute('data-sandbox', '');

    const banner = document.createElement('div');
    banner.className = 'sandbox-mode-banner';
    banner.textContent = 'Test Mode – Simulated Payment';
    container.appendChild(banner);
  }

  const closeBtn = document.createElement('button');
  closeBtn.id = CLOSE_BTN_ID;
  closeBtn.type = 'button';
  closeBtn.textContent = '✕';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.addEventListener('click', () => closeModal(true));

  const spinner = createSpinner();

  const iframe = document.createElement('iframe');
  iframe.id = IFRAME_ID;
  iframe.src = url;
  iframe.title = 'Arto Pay secure checkout';
  iframe.setAttribute('allow', 'payment; clipboard-write');

  iframe.addEventListener('load', () => {
    spinner.classList.add('hidden');
    sendInitToIframe(); // fallback kalau READY tidak ke-trigger
  });

  container.appendChild(closeBtn);
  container.appendChild(spinner);
  container.appendChild(iframe);
  modal.appendChild(backdrop);
  modal.appendChild(container);
  document.body.appendChild(modal);

  // Focus lands on the dialog itself (not the iframe) so Escape keeps reaching this page.
  makeBackgroundInert(modal);
  container.focus({ preventScroll: true });

  messageHandler = handleMessage;
  keyHandler = handleKeydown;

  window.addEventListener('message', messageHandler);
  document.addEventListener('keydown', keyHandler);

  setState({ isOpen: true, url });
}

export function closeModal(triggerOnClose = true): void {
  const callbacks = getCallbacks();
  const modal = document.getElementById(MODAL_ID);
  if (!modal) return;

  if (messageHandler) {
    window.removeEventListener('message', messageHandler);
    messageHandler = null;
  }

  if (keyHandler) {
    document.removeEventListener('keydown', keyHandler);
    keyHandler = null;
  }

  modal.remove();
  unlockBodyScroll();
  restoreBackground();
  restoreFocus();
  resetState();

  if (triggerOnClose) {
    callbacks.onClose?.();
  }
}
